/**
 * Helima consolidation — public booking slug override (admin UI).
 *
 * On the consolidated instance each doctor was re-slugged to a unique username
 * (fuertes / ivelise / ellianny) to avoid the shared "cita" collision. But their
 * patients know the legacy public path `app.<doctor>.com/cita`, and the Cloudflare
 * front-door maps `/cita` -> the real username. So the admin's "public page" /
 * preview / copy-link should display the legacy slug ("cita"), not the internal
 * username.
 *
 * This ONLY affects the public link shown/copied in the panel — routing, API calls
 * and the real profile still use the true username.
 *
 * On the consolidated fork the public slug is the CONSTANT "cita" for every doctor
 * (no per-doctor data), so onboarding a new doctor needs no change here.
 */

// Helima consolidation: on the single consolidated instance EVERY doctor's public
// booking page is served at the shared legacy path "/cita" (the middleware resolves
// /cita -> the real username by host; the front-door keeps the doctor domain). So
// the panel's "public page"/copy-link should ALWAYS display "cita", regardless of
// the internal username. This is a constant of the consolidated fork — not
// per-doctor data — so onboarding a new doctor needs no change here and no rebuild.
const HELIMA_PUBLIC_SLUG = "cita";

/**
 * Returns the public-facing booking slug ("cita") for any doctor on the
 * consolidated instance. Empty input is passed through unchanged.
 */
export function getHelimaPublicSlug(username: string | null | undefined): string {
  if (!username) return username ?? "";
  return HELIMA_PUBLIC_SLUG;
}

/**
 * Base URL for the admin panel's public-page/preview/copy links: the account's own domain
 * (`homeUrl`, from the session) when it has one. Every Helima domain serves the same panel, so the
 * browser's current origin can be another professional's domain, whose /cita is their page.
 *
 * Without a home domain (instance admins, accounts not in the registry) it falls back to the
 * CURRENT origin, so links stay on the domain the panel was opened from rather than depending on
 * the front-door rewrite of the baked WEBAPP_URL/bookerUrl. SSR (no window) uses
 * `fallbackBaseUrl`, which the front-door still rewrites in the delivered HTML.
 */
export function getHelimaPublicBaseUrl(fallbackBaseUrl: string, homeUrl?: string | null): string {
  if (homeUrl) return homeUrl;
  if (typeof window !== "undefined" && window.location?.origin) return window.location.origin;
  return fallbackBaseUrl;
}
